// cmp_app_shell left navigation rail — renders the SINGLE grouped menu
// (SEM-5131 owner decision 2026-07-30: no hat, no menu swap). WHICH entries a
// caller sees, and why, belongs to ./use-visible-menu; this file renders that
// answer, folds it, and lights the entry the current URL belongs to. Labels
// are i18n keys (ADR-006).
//
// Faz 0.3 (novem port): rail carries the novem shell language — brand
// header (wordmark + italic serif tagline), emerald active state
// (accent-subtle bg + accent-800 text per mockup Sidebar), oat canvas,
// user footer with avatar + sign-out.

import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronsLeft, ChevronsRight, LogOut, Search, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { useAuth } from "react-oidc-context";
import { useQueryClient } from "@tanstack/react-query";

import { Avatar, Drawer } from "@novemai/components";

import { signOut } from "@/auth/session-tabs";
import { useSessionTermination } from "@/auth/session-termination";
import { cn } from "@/lib/utils";
import { CompanySwitcher } from "./CompanySwitcher";
import { menuLinks, orderSectionsByAuthority, type MenuItem } from "./menu-config";
import { useVisibleMenu } from "./use-visible-menu";
import { useUiStore } from "@/store/ui";

// Daily management links stay visible; personal and payroll groups never fold.
const SECTION_PREVIEW_COUNT = 10;

/**
 * Which single rail entry the current URL belongs to — LONGEST MATCH WINS.
 *
 * Not NavLink's default, and the difference is the whole point. Prefix matching
 * is right for a destination whose children are NOT in the menu: `/people/42`
 * should keep "Çalışanlar" lit, because there is no better answer. It is wrong
 * the moment a child IS its own entry — `/settings/approval-chains` lit both
 * itself and `/settings`, so the rail showed two green rows for one location
 * and stopped answering "where am I".
 *
 * Passing the visible hrefs rather than the whole menu is deliberate: if a
 * child is hidden from this caller, its nearest VISIBLE ancestor is the honest
 * answer, not nothing at all. Depth is never assumed — the rule is the same at
 * any nesting level.
 */
function isPathActive(
  pathname: string,
  href: string,
  hrefs: readonly string[],
): boolean {
  if (href === "/") return pathname === "/";
  if (pathname === href) return true;
  if (!pathname.startsWith(`${href}/`)) return false;
  return !hrefs.some(
    (other) =>
      other.length > href.length &&
      (pathname === other || pathname.startsWith(`${other}/`)),
  );
}

/**
 * Viewport at or above `minWidthPx`. Breakpoints are the design-token ones
 * (44-design-tokens-concrete `breakpoints`: md 768, lg 1024); the first render
 * reads `innerWidth` so a tablet does not paint the wide rail for one frame.
 */
function useMinWidth(minWidthPx: number): boolean {
  const [matches, setMatches] = useState(() => window.innerWidth >= minWidthPx);
  useEffect(() => {
    const media = window.matchMedia(`(min-width: ${String(minWidthPx)}px)`);
    const update = (): void => { setMatches(media.matches); };
    media.addEventListener("change", update);
    return () => { media.removeEventListener("change", update); };
  }, [minWidthPx]);
  return matches;
}

export function LeftRail(): React.ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const termination = useSessionTermination();
  const { pathname } = useLocation();
  const preferredExpanded = useUiStore((s) => s.leftRailExpanded);
  const toggleLeftRail = useUiStore((s) => s.toggleLeftRail);
  // Three shapes (spec 46 app_shell.responsive_behavior): below md the rail is
  // a drawer, md is icon-only, lg and up carries labels. The lg preference and
  // the md override are separate on purpose: someone who widened the rail on a
  // tablet must not find the desktop rail reshaped when they open the laptop,
  // and the reverse.
  const desktop = useMinWidth(768);
  const wide = useMinWidth(1024);
  const [openBelowLg, setOpenBelowLg] = useState(false);
  // A user can close an automatically opened group on the current page. Nested
  // entries keep the same rule, keyed by item, so closing "İzinlerim" while on
  // its calendar page sticks until the page changes.
  const [sectionOverrides, setSectionOverrides] = useState<Record<string, { path: string; open: boolean }>>({});
  const [itemOverrides, setItemOverrides] = useState<Record<string, { path: string; open: boolean }>>({});
  const setGlobalSearchOpen = useUiStore((s) => s.setGlobalSearchOpen);
  const expanded = !desktop || (wide ? preferredExpanded : openBelowLg);
  const mobileOpen = useUiStore((s) => s.mobileNavOpen);
  const setMobileNavOpen = useUiStore((s) => s.setMobileNavOpen);
  const expandedSections = useUiStore((s) => s.expandedRailSections);
  const toggleSection = useUiStore((s) => s.toggleRailSection);
  const { sections: visibleSections, resolved, failed } = useVisibleMenu();
  // The candidate set the longest-match rule discriminates against. Flattened
  // across sections, not per section: `/settings` and its children can sit in
  // different groups, and a rule that could not see across groups would light
  // both again.
  const visibleHrefs = useMemo(
    () => visibleSections.flatMap((section) => menuLinks(section.items).map((item) => item.href)),
    [visibleSections],
  );

  // Gidilen yer değişince panel kapanır: dar ekranda panel içeriğin ÜSTÜNDE
  // duruyor, açık bırakmak kullanıcının tıkladığı sayfayı gizlemek olurdu.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname, setMobileNavOpen]);

  useEffect(() => {
    if (desktop) setMobileNavOpen(false);
  }, [desktop, setMobileNavOpen]);

  const matchingSections = orderSectionsByAuthority(visibleSections);
  // An entry is "here" when its own page or one of its sub-pages is.
  const entryActive = (item: MenuItem): boolean =>
    (item.href !== undefined && isPathActive(pathname, item.href, visibleHrefs)) ||
    (item.children ?? []).some((child) => isPathActive(pathname, child.href, visibleHrefs));
  const shortcutLabel = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl K";

  const linkClass = (active: boolean, nested = false): string => cn(
    "flex min-h-10 items-center gap-2.5 rounded-[8px] px-2.5 py-2 font-sans font-medium no-underline max-md:min-h-11",
    nested ? "text-[13px] text-[var(--fg-secondary)]" : "text-sm text-[var(--fg-primary)]",
    "hover:bg-[var(--bg-subtle)]",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
    active &&
      "bg-[var(--accent-subtle-bg)] text-[var(--accent-active)] hover:bg-[var(--accent-subtle-bg)] [&_svg]:text-[var(--accent)]",
    !expanded && "justify-center",
  );

  // Link, NOT NavLink. NavLink's `isActive` cannot see the sibling entries the
  // longest-match rule needs, and it injects its OWN aria-current from that
  // same wrong answer — so while this file computed the colour correctly, the
  // rail still told a screen reader it was in two places at once. One source
  // of truth for "am I here": isPathActive.
  const renderLink = (key: string, labelKey: string, href: string, Icon: LucideIcon | undefined, nested = false): React.ReactElement => {
    const active = isPathActive(pathname, href, visibleHrefs);
    return (
      <Link
        key={key}
        to={href}
        aria-label={!expanded ? t(labelKey) : undefined}
        title={t(labelKey)}
        onClick={(): void => { setMobileNavOpen(false); }}
        aria-current={active ? "page" : undefined}
        className={cn(linkClass(active, nested), "min-w-0 flex-1")}
      >
        {Icon !== undefined && <Icon size={17} aria-hidden="true" className="shrink-0 text-[var(--fg-muted)]" />}
        {expanded && <span className="min-w-0 break-words leading-5">{t(labelKey)}</span>}
      </Link>
    );
  };

  const renderEntry = (item: MenuItem): React.ReactElement => {
    const children = item.children ?? [];
    // The icon-only rail has nowhere to unfold into: every destination renders
    // flat, and a heading-only parent ("İzinlerim") is just its pages.
    if (!expanded || children.length === 0) {
      return (
        <div key={item.key} className="contents">
          {item.href !== undefined && renderLink(item.key, item.labelKey, item.href, item.icon)}
          {children.map((child) => renderLink(child.key, child.labelKey, child.href, child.icon))}
        </div>
      );
    }
    const here = entryActive(item);
    const override = itemOverrides[item.key];
    const open = override?.path === pathname ? override.open : here;
    const childListId = `navigation-item-${item.key}`;
    const toggle = (): void => {
      setItemOverrides((current) => ({ ...current, [item.key]: { path: pathname, open: !open } }));
    };
    const chevron = (
      <ChevronDown
        size={14}
        aria-hidden="true"
        className={cn("shrink-0 text-[var(--fg-muted)] transition-transform", !open && "-rotate-90")}
      />
    );
    return (
      <div key={item.key} className="flex flex-col gap-px">
        {item.href === undefined ? (
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-controls={childListId}
            className={cn(linkClass(false), "w-full text-left", here && "text-[var(--accent-active)] [&_svg]:text-[var(--accent)]")}
          >
            <item.icon size={17} aria-hidden="true" className="shrink-0 text-[var(--fg-muted)]" />
            <span className="min-w-0 flex-1 break-words leading-5">{t(item.labelKey)}</span>
            {chevron}
          </button>
        ) : (
          <div className="flex items-center gap-px">
            {renderLink(item.key, item.labelKey, item.href, item.icon)}
            <button
              type="button"
              onClick={toggle}
              aria-expanded={open}
              aria-controls={childListId}
              aria-label={t(open ? "nav.collapse_item" : "nav.expand_item", { label: t(item.labelKey) })}
              className="flex h-10 w-9 shrink-0 items-center justify-center rounded-[8px] hover:bg-[var(--bg-subtle)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] max-md:h-11"
            >
              {chevron}
            </button>
          </div>
        )}
        {open && (
          <div id={childListId} className="ml-[22px] flex flex-col gap-px border-l border-[var(--border-subtle)] pl-2">
            {children.map((child) => renderLink(child.key, child.labelKey, child.href, undefined, true))}
          </div>
        )}
      </div>
    );
  };

  const userLabel =
    auth.user?.profile.name ??
    auth.user?.profile.preferred_username ??
    auth.user?.profile.sub ??
    "user";

  const rail = (
      <aside
        className={cn(
          "flex min-h-0 flex-col border-r border-[var(--border-subtle)] bg-[var(--bg-surface)]",
          desktop ? "shrink-0" : "h-full w-full",
          desktop && (expanded ? "w-60" : "w-16"),
        )}
        id="primary-navigation"
        aria-label={t("nav.primary_label")}
      >
      {/* Brand header — "Novem HR" wordmark + italic serif tagline. Wordmark
          halves come from the bundle (identical across locales) so the
          no-literal lint stays authoritative everywhere else. The space
          between the halves is deliberate: without it the rail reads
          "NovemHR", a name the product does not have (spec 53, E17). */}
      <div className={cn("flex items-center gap-2.5 px-4 pb-4 pt-5", !expanded && "justify-center px-2")}>
        <div
          aria-hidden="true"
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[8px] bg-[var(--accent)] font-sans text-[13px] font-bold text-[var(--fg-on-accent)]"
        >
          {t("app.wordmark_base").slice(0, 1)}
        </div>
        {expanded && (
          <div className="leading-none">
            <div className="font-sans text-[17px] font-semibold tracking-[-0.02em] text-[var(--fg-primary)]">
              {t("app.wordmark_base")}{" "}
              <span className="text-[var(--accent)]">{t("app.wordmark_accent")}</span>
            </div>
            <div className="mt-0.5 font-serif text-[11px] italic text-[var(--fg-muted)]">
              {t("app.tagline")}
            </div>
          </div>
        )}
        {!desktop && (
          <button
            type="button"
            aria-label={t("nav.close_menu")}
            onClick={(): void => { setMobileNavOpen(false); }}
            className="ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-[8px] text-[var(--fg-secondary)] hover:bg-[var(--bg-subtle)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
          >
            <X size={18} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Yalnız geniş/açık çubukta: daralmış çubuk 64px ve seçici orada şirket
          adını gösteremez — adsız bir bağlam anahtarı, hangi bağlamda olduğunu
          söylemeyen bir bağlam anahtarıdır. */}
      {expanded && <CompanySwitcher />}

      {/* The box opens the ⌘K record search: the drawing's rail search is the
          global one, and the rail's own pages are searched inside it. */}
      {expanded && (
        <div className="px-2.5 pb-2">
          <button
            type="button"
            aria-haspopup="dialog"
            aria-keyshortcuts="Meta+K Control+K"
            onClick={(): void => { setMobileNavOpen(false); setGlobalSearchOpen(true); }}
            className="flex min-h-11 w-full items-center gap-2 rounded-[8px] border border-[var(--border-default)] bg-[var(--bg-raised)] px-2.5 text-left text-sm text-[var(--fg-secondary)] hover:bg-[var(--bg-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
          >
            <Search size={16} aria-hidden="true" className="shrink-0" />
            <span className="min-w-0 flex-1 truncate">{t("nav.search_open")}</span>
            <kbd aria-hidden="true" className="shrink-0 rounded-[4px] bg-[var(--bg-subtle)] px-1.5 py-0.5 font-mono text-[11px] text-[var(--fg-muted)]">{shortcutLabel}</kbd>
          </button>
        </div>
      )}
      <nav data-tour="shell-rail" aria-busy={!resolved && !failed} className="flex flex-1 flex-col gap-1 overflow-y-auto px-2.5 pb-2">
        {/* Only authorized groups participate in ordering. */}
        {matchingSections.map((section) => {
          const hiddenCount = section.items.length - SECTION_PREVIEW_COUNT;
          // The icon-only rail has no headings to click, so nothing there can
          // reveal a folded item — it renders whole, in EITHER mode.
          const headingKey = expanded ? section.sectionLabelKey : undefined;
          const groupMode = headingKey !== undefined && section.collapseMode === "group";
          // A group that folds as a whole must not ALSO truncate its tail: the
          // two controls would stack and the second would only ever appear
          // after the user opened the first.
          const collapsible = headingKey !== undefined && !groupMode && section.collapseMode !== "none" && hiddenCount > 0;
          // Where the fold starts — what folds away is a TAIL in both modes:
          // the heading toggle folds the whole group, the "show more" control
          // folds everything past the preview, an unfoldable group folds
          // nothing.
          const foldFrom = groupMode
            ? 0
            : collapsible
              ? SECTION_PREVIEW_COUNT
              : section.items.length;
          const folded = section.items.slice(foldFrom);
          // One rule for both modes: opened by the user, OR the current page
          // sits in what is folded away — hiding the link the user is standing
          // on would read as the rail losing track of them, which is worse than
          // a long list.
          const override = sectionOverrides[section.key];
          const open = folded.length === 0 || (
            override?.path === pathname ? override.open :
            expandedSections.has(section.key) ||
            folded.some(entryActive)
          );
          // The active tail replaces the last preview slot, keeping ten rows
          // and a truthful hidden count even when only one item was folded.
          const activeTail = !groupMode && folded.some(entryActive);
          const previewCount = foldFrom - (activeTail ? 1 : 0);
          const items = open ? section.items : section.items.filter((item, index) =>
            index < previewCount || (!groupMode && entryActive(item)),
          );
          // eslint-disable-next-line i18next/no-literal-string -- DOM id for aria-controls, never visible copy.
          const listId = `navigation-${section.key}`;
          const toggle = (): void => {
            setSectionOverrides((current) => ({ ...current, [section.key]: { path: pathname, open: !open } }));
            if (expandedSections.has(section.key) === open) toggleSection(section.key);
          };

          return (
            <div key={section.key} className="flex flex-col gap-px">
              {headingKey !== undefined &&
                (groupMode ? (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-expanded={open}
                  aria-controls={listId}
                    className={cn(
                      "mt-2 flex min-h-11 items-center gap-1.5 rounded-[8px] border border-[var(--border-default)] bg-[var(--bg-subtle)] px-2.5 py-2 text-left",
                      // Düğmenin kendi zemini bg-subtle; eski hover da bg-subtle
                      // olduğu için üzerine gelme görünmüyordu. bg-surface spec 44'te
                      // iki temada da bg-subtle'dan ayrışır.
                      "hover:bg-[var(--bg-surface)]",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
                    )}
                  >
                    <ChevronDown
                      size={14}
                      aria-hidden="true"
                      className={cn(
                        "shrink-0 text-[var(--fg-muted)] transition-transform",
                        !open && "-rotate-90",
                      )}
                    />
                    <span className="nai-section-label text-[var(--fg-primary)]">{t(headingKey)}</span>
                  </button>
                ) : (
                  <p className="nai-section-label px-2.5 pb-1.5 pt-3.5">{t(headingKey)}</p>
                ))}
              <div id={listId} className="flex flex-col gap-px">
              {items.map(renderEntry)}
              </div>
              {/* One control, and it names the size of what it hides: a bare
                  chevron on the heading would toggle the same thing while
                  leaving "how much am I not seeing" unanswered — which is the
                  question a truncated nav creates. */}
              {collapsible && (
                <button
                  type="button"
                  onClick={toggle}
                  aria-expanded={open}
                  aria-controls={listId}
                  className={cn(
                    "mt-1 flex min-h-11 items-center gap-2 rounded-[8px] border border-[var(--border-default)] bg-[var(--bg-subtle)] px-2.5 py-2 text-left",
                    "font-sans text-sm font-semibold text-[var(--fg-primary)]",
                    "hover:bg-[var(--bg-surface)]",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
                  )}
                >
                  <ChevronDown
                    size={15}
                    aria-hidden="true"
                    className={cn("shrink-0 transition-transform", !open && "-rotate-90")}
                  />
                  <span className="truncate">
                    {open
                      ? t("menu.section.show_less")
                      : t("menu.section.show_more", { count: section.items.length - items.length })}
                  </span>
                </button>
              )}
            </div>
          );
        })}
      </nav>

      {/* User footer — avatar + name + sign-out (mockup Sidebar footer) */}
      {auth.isAuthenticated && (
        <div
          className={cn(
            "flex items-center gap-2.5 border-t border-[var(--border-subtle)] px-3 py-3",
            !expanded && "flex-col px-2",
          )}
        >
          <Avatar
            alt={userLabel}
            size={32}
            initials={userLabel
              .split(" ")
              .map((p) => p[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          />
          {expanded && (
            <div className="min-w-0 flex-1">
              <div className="truncate font-sans text-[13px] font-medium text-[var(--fg-primary)]">
                {userLabel}
              </div>
            </div>
          )}
          {/* Çıkış daralmış çubukta da görünür kalmak zorunda: md'de çubuk
              kendiliğinden ikon şeridine iniyor ve çıkışın tek yeri burası.
              Adı görünmeyen düğme title + aria-label taşıyor. */}
          <button
            type="button"
            onClick={(): void => signOut(auth, queryClient, termination)}
            aria-label={t("auth.sign_out")}
            title={!expanded ? t("auth.sign_out") : undefined}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[6px] text-[var(--fg-secondary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--fg-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
          >
            <LogOut size={15} aria-hidden="true" />
          </button>
          {/* Daraltma düğmesi (spec 46 app_shell: "collapse toggle at bottom").
              Drawer modunda yok: orada çubuğun açık/kapalı hali menü düğmesinin
              işi. md'de ikon şeridini geçici olarak açmanın tek yolu — etiketler,
              şirket seçici ve dokunmatik ekranda tooltip'siz kalan ikonlar için. */}
          {desktop && (
            <button
              type="button"
              onClick={(): void => {
                if (wide) toggleLeftRail();
                else setOpenBelowLg((open) => !open);
              }}
              aria-expanded={expanded}
              aria-controls="primary-navigation"
              aria-label={t(expanded ? "nav.collapse_rail" : "nav.expand_rail")}
              title={t(expanded ? "nav.collapse_rail" : "nav.expand_rail")}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[6px] text-[var(--fg-secondary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--fg-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            >
              {expanded ? (
                <ChevronsLeft size={15} aria-hidden="true" />
              ) : (
                <ChevronsRight size={15} aria-hidden="true" />
              )}
            </button>
          )}
        </div>
      )}
      </aside>
  );

  if (desktop) return rail;
  return (
    <Drawer
      open={mobileOpen}
      onClose={(): void => { setMobileNavOpen(false); }}
      side="left"
      aria-label={t("nav.primary_label")}
      className="nai-mobile-navigation"
    >
      {rail}
    </Drawer>
  );
}
